import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { brandNames } from 'data/schema';
import type { IBrand, IMarket } from 'data/schema';
import { marketNames } from 'data/marketNames';
import type { IDealerIdOption, IDealerSelectionError, TDealerUnavailableReason } from './dealerResolution.types';
import { parseDealerId } from './dealerIdFormat';
import { hasAvailableDealerId } from './dealerIdOptions';

/** Human-readable brand and market, e.g. "Audi Netherlands"; falls back to the raw codes when unknown. */
export function getBrandMarketLabel(brand: IBrand, market: IMarket): string {
  return `${brandNames[brand] ?? brand} ${marketNames[market] ?? market}`;
}

/** Message shown in the dealer selection dialog for a rejected dealer ID. */
export function getDealerSelectionErrorMessage(
  error: IDealerSelectionError,
  brand: IBrand,
  market: IMarket
): MessageDescriptor {
  const { dealerId } = error;

  switch (error.reason) {
    case 'not-owned':
      return msg({
        message: `Dealer ID ${dealerId} does not belong to your account. Select a different Dealer ID.`,
      });
    case 'invalid-format':
      return msg({
        message: `Dealer ID ${dealerId} is not in a valid format. Select a different Dealer ID.`,
      });
    case 'brand-market-mismatch': {
      const brandAndMarket = getBrandMarketLabel(brand, market);

      return msg({
        message: `Dealer ID ${dealerId} is not valid for ${brandAndMarket}. Select a different Dealer ID.`,
        comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
      });
    }
    case 'market-disabled': {
      const brandAndMarket = getOwnBrandMarketLabel(dealerId, brand, market);

      return msg({
        message: `Dealer ID ${dealerId} cannot be used because ${brandAndMarket} is not enabled in 3D Exhibition yet. Select a different Dealer ID.`,
        comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
      });
    }
    case 'not-authorized': {
      const brandAndMarket = getOwnBrandMarketLabel(dealerId, brand, market);

      return msg({
        message: `Dealer ID ${dealerId} is not authorized for ${brandAndMarket}. Select a different Dealer ID or contact your administrator.`,
        comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
      });
    }
  }
}

/** The Dealer ID's own brand and market when it parses, else the brand and market passed in. */
function getOwnBrandMarketLabel(dealerId: string, fallbackBrand: IBrand, fallbackMarket: IMarket): string {
  const parsed = parseDealerId(dealerId);

  return getBrandMarketLabel(parsed?.brand ?? fallbackBrand, parsed?.market ?? fallbackMarket);
}

/** Short label for a ruled-out Dealer ID's tag in the window. */
export function getDealerUnavailableLabel(reason: TDealerUnavailableReason): MessageDescriptor {
  return reason === 'market-disabled' ? msg({ message: 'Market not enabled' }) : msg({ message: 'Not authorized' });
}

/** The red no-fit line: none of the account's Dealer IDs fits the URL's brand and market. */
export function getNoDealerIdForBrandMarketMessage(brand: IBrand, market: IMarket): MessageDescriptor {
  const brandAndMarket = getBrandMarketLabel(brand, market);

  return msg({
    message: `None of your Dealer IDs is valid for ${brandAndMarket}. Select a different Dealer ID.`,
    comment: 'brandAndMarket is a brand and country name, for example "Audi Netherlands"',
  });
}

/** The red nothing-left line: every listed Dealer ID is ruled out for this visit. */
export function getNoDealerIdAvailableMessage(): MessageDescriptor {
  return msg({ message: 'None of your Dealer IDs can be used right now. Contact your administrator.' });
}

interface IDealerWindowMessageInput {
  options: readonly IDealerIdOption[];
  error?: IDealerSelectionError;
  brand: IBrand;
  market: IMarket;
}

/**
 * The window's single red message line, in priority order:
 * nothing-left, else the given reason, else the no-fit notice, else nothing.
 */
export function getDealerWindowMessage({
  options,
  error,
  brand,
  market,
}: IDealerWindowMessageInput): MessageDescriptor | undefined {
  if (options.length > 0 && !hasAvailableDealerId(options)) {
    return getNoDealerIdAvailableMessage();
  }

  if (error) {
    return getDealerSelectionErrorMessage(error, brand, market);
  }

  if (!options.some((option) => option.fitsContext)) {
    return getNoDealerIdForBrandMarketMessage(brand, market);
  }

  return undefined;
}
